import { Injectable, ChangeDetectorRef } from '@angular/core';
import { TemplateControlType } from 'src/app/modules/management/store/communication-center';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import {
  ImageControl,
  SectionHeaderControl,
  SettingsWrapperControl,
  TextControl,
  DividerControl,
  ButtonControl,
  ColumnsControl,
  FormFieldControl,
  AttachmentsControl,
  MapControl,
  SelectedAssetsListControl,
  TableRowControl,
  TableControl
} from './';
import { Subject } from 'rxjs';
import { GlobalEventListenerService } from 'src/app/modules/core/services/global-event-listener.service';
import { IDesignControl } from './design-control';
import { v4 } from 'uuid';
import { TimelineControl } from './timeline-control';

@Injectable({
  providedIn: 'root'
})
export class TemplateControlService {
  allDesignControls: IDesignControl[] = [];
  controlDefinitions = {
    [TemplateControlType.Image]: ImageControl,
    [TemplateControlType.SectionHeader]: SectionHeaderControl,
    [TemplateControlType.Text]: TextControl,
    [TemplateControlType.Divider]: DividerControl,
    [TemplateControlType.Button]: ButtonControl,
    [TemplateControlType.Columns]: ColumnsControl,
    [TemplateControlType.FormField]: FormFieldControl,
    [TemplateControlType.Attachments]: AttachmentsControl,
    [TemplateControlType.Map]: MapControl,
    [TemplateControlType.SelectedAssetsList]: SelectedAssetsListControl,
    [TemplateControlType.TableRow]: TableRowControl,
    [TemplateControlType.Table]: TableControl,
    [TemplateControlType.Timeline]: TimelineControl,
  };

  public get settingsWrapperControl(): SettingsWrapperControl {
    if (!this._settingsWrapperControl) {
      const wrapperElement = this.document.getElementById('settings-wrapper-control');
      this._settingsWrapperControl = new SettingsWrapperControl(wrapperElement, this.document, this.eventListenerService, this);
    }

    return this._settingsWrapperControl;
  }
  private _settingsWrapperControl: SettingsWrapperControl = null;
  public rootControl: IDesignControl;
  public document: Document;
  public dataset: Dataset;
  public isPreviewLayout: boolean = false;
  public enableDrag: boolean = true;
  public richText: string;
  public isRichTextActive: boolean = false;
  public richTextUpdates$: Subject<string> = new Subject<string>();
  public richTextCancel$: Subject<void> = new Subject<void>();
  public changeDetector: ChangeDetectorRef;

  constructor(private eventListenerService: GlobalEventListenerService) {}

  initializeDesignSurface(document: Document) {
    this._settingsWrapperControl = null;
    this.document = document;
    this.allDesignControls.map(c => c.destroy());
    this.allDesignControls = [];
    this.isPreviewLayout = false;

    const designSurface = this.document.getElementById('design-surface') as HTMLElement;

    this.rootControl = new TableControl(this.document, this);
    this.rootControl.id = 'message-template';
    this.rootControl.controlType = TemplateControlType.Table;
    this.rootControl.appendControl(designSurface);
    this.rootControl.wireUpEvents();

    this.allDesignControls.push(this.rootControl);
  }

  initializeFromLayoutTemplate(document: Document, templateHtml: string, isPreviewLayout: boolean = false) {
    this._settingsWrapperControl = null;
    this.document = document;
    this.allDesignControls.map(c => c.destroy());
    this.allDesignControls = [];
    this.rootControl = null;
    this.isPreviewLayout = isPreviewLayout;

    const designSurface = document.getElementById('design-surface') as HTMLElement;
    const container = document.createElement('template');

    if (!designSurface || !container) {
      return;
    }

    container.innerHTML = templateHtml;
    const messageTemplate = container.content.firstElementChild as HTMLElement;
    designSurface.appendChild(messageTemplate);

    const controlElements = designSurface.querySelectorAll(
      'table[data-control-type],tr[data-control-type],td[data-control-type],div[data-control-type]'
    );

    controlElements.forEach((c: HTMLElement) => {
      const id = c.dataset.id;
      const controlType = TemplateControlType[c.dataset.controlType];
      const ControlTypeDef = this.controlDefinitions[controlType];

      const controlToAttach = new ControlTypeDef(this.document, this);
      controlToAttach.id = id;
      controlToAttach.controlType = controlType;
      controlToAttach.controlElement = c;
      controlToAttach.wireUpEvents();

      this.registerControl(controlToAttach);

      if (!this.rootControl && controlToAttach.controlType === TemplateControlType.Table) {
        this.rootControl = controlToAttach;
      }
    });
  }

  addControlToRoot(controlType: TemplateControlType): IDesignControl {
    const newControl = this.addChildControlToParentControl(this.rootControl, controlType);

    return newControl;
  }

  addChildControlToParentControl(parentControl: IDesignControl, controlType: TemplateControlType): IDesignControl {
    const ControlTypeDef = this.controlDefinitions[controlType];

    const control = new ControlTypeDef(this.document, this);
    control.id = v4();
    control.controlType = controlType;

    control.appendControl(parentControl.childContentElement);
    control.wireUpEvents();

    this.registerControl(control);

    return control;
  }

  insertSiblingControl(siblingControl: IDesignControl, controlType: TemplateControlType): IDesignControl {
    let row = siblingControl.controlElement;
    while (!!row) {
      if (row.nodeName === 'TR') {
        break;
      }
      row = row.parentElement;
    }

    const ControlTypeDef = this.controlDefinitions[controlType];

    const rowControl = new TableRowControl(this.document, this);
    rowControl.id = v4();
    rowControl.controlType = TemplateControlType.TableRow;
    rowControl.insertSiblingControl(row);
    rowControl.wireUpEvents();

    this.registerControl(rowControl);

    const control = new ControlTypeDef(this.document, this);
    control.id = v4();
    control.controlType = controlType;
    control.appendControl(rowControl.childContentElement);
    control.wireUpEvents();

    this.registerControl(control);

    return control;
  }

  removeControl(control: IDesignControl) {
    // we need to find the cell and row that contains
    // this control and remove them as well.
    const cell = control.controlElement.parentElement;
    const cellId = cell.dataset.id;
    if (!!cellId) {
      const cellIndex = this.allDesignControls.findIndex(c => c.id === cellId);
      const cellControl = this.allDesignControls.splice(cellIndex, 1)[0];
      cellControl?.destroy();
    }

    const row = cell.parentElement;
    const rowId = row.dataset.id;
    if (!!rowId) {
      const rowIndex = this.allDesignControls.findIndex(c => c.id === rowId);
      const rowControl = this.allDesignControls.splice(rowIndex, 1)[0];
      rowControl?.destroy();
    }

    const controlIndex = this.allDesignControls.findIndex(c => c.id === control.id);
    this.allDesignControls.splice(controlIndex, 1)[0];

    control.destroy();
  }

  copyControl(control: IDesignControl): IDesignControl {
    const row = control.controlElement.parentElement.parentElement;
    const controlHtml = row.outerHTML;

    const container = document.createElement('template');
    container.innerHTML = controlHtml;
    const copiedElement = container.content.firstElementChild as HTMLElement;
    const controls = container.content.querySelectorAll(
      'table[data-control-type],tr[data-control-type],td[data-control-type],div[data-control-type]'
    );

    let copiedControl: IDesignControl;
    controls.forEach((c: HTMLElement) => {
      const isMainControl = c.id === control.id;

      const id = v4();
      c.setAttribute('data-id', id);
      c.id = id;

      const controlType = TemplateControlType[c.dataset.controlType];
      const ControlTypeDef = this.controlDefinitions[controlType];

      const controlToAttach = new ControlTypeDef(this.document, this);

      controlToAttach.id = id;
      controlToAttach.controlType = controlType;
      controlToAttach.controlElement = c;
      controlToAttach.wireUpEvents();

      this.registerControl(controlToAttach);

      if (isMainControl) {
        copiedControl = controlToAttach;
      }
    });

    row.insertAdjacentElement('afterend', copiedElement);

    return copiedControl;
  }

  registerControl(control: IDesignControl) {
    this.allDesignControls.push(control);
  }

  moveControl(sourceElementId: string, control: IDesignControl) {
    // find the row that is going to be moved
    // and remove it from the dom
    let elmToMove = document.getElementById(sourceElementId);
    while (!!elmToMove) {
      if (elmToMove.nodeName === 'TR') {
        break;
      }
      elmToMove = elmToMove.parentElement;
    }

    if (!elmToMove) {
      return;
    }

    elmToMove.parentElement.removeChild(elmToMove);

    // find the destination and insert the row
    // after the control
    let destination = control.controlElement;
    while (!!destination) {
      if (destination.nodeName === 'TR') {
        break;
      }
      destination = destination.parentElement;
    }

    if (!destination) {
      return;
    }

    destination.insertAdjacentElement('afterend', elmToMove);
  }

  hideSettingsControlIfNotEdit() {
    if (!!this._settingsWrapperControl && !this._settingsWrapperControl.isEditMode) {
      this._settingsWrapperControl.hide();
      this.changeDetector?.markForCheck();
    }
  }

  editRichText(richText: string) {
    this.richText = richText;
    this.isRichTextActive = true;
    this.changeDetector?.markForCheck();
  }

  updateRichText(updatedRichText: string) {
    this.richTextUpdates$.next(updatedRichText);
    this.richText = updatedRichText;
    this.isRichTextActive = false;
    this.changeDetector?.markForCheck();
  }

  cancelRichText() {
    this.richTextCancel$.next();
    this.isRichTextActive = false;
    this.changeDetector?.markForCheck();
  }
}
